import { isAxiosError } from 'axios';
import { t } from '@lingui/macro';

import { IsValidDanishCVR } from '~/app/shared/util/utilities/cvrUtils';
import { PaymentCardsApi } from '~/app/pages/accounting/payment-cards/api';
import PaymentCardsOnboardingDialogStep1Pricing from './Step1Pricing';
import PaymentCardsOnboardingDialogCompanyStep2CompanyInformation from './Step2CompanyInformation';
import PaymentCardsOnboardingDialogStep3Partner from './Step3Partner';
import {
    type CompanyInformation,
    type CompanyInputProps,
    OnboardingStepEnum,
    type PaymentCardsOnboardingDialogState,
    StepConfigs,
} from '../types';

interface Options {
    api: PaymentCardsApi;
    onClose: () => void;
}

export const isDenmark = (country: string) => country === 'DK' || country === 'Denmark' || country === 'Danmark';

export const isValidPaymentCardsOnboardingFormState = (formData: PaymentCardsOnboardingDialogState['formData']) => {
    return (
        formData.companyName &&
        isDenmark(formData.country) &&
        formData.cvr &&
        formData.cvr.length === 8 &&
        formData.agreeWithTerms
    );
};

export const getStepConfig = ({ api, onClose }: Options) => {
    const stepsConfig: StepConfigs = {
        [OnboardingStepEnum.STEP1]: {
            prev: {
                text: t`Close`,
                stateOnClick: () => {
                    onClose();
                    return Promise.resolve(null);
                },
            },
            next: {
                text: t`Next`,
                stateOnClick: () =>
                    Promise.resolve({
                        step: OnboardingStepEnum.STEP2,
                        error: '',
                        validationError: '',
                    }),
            },
            index: 1,
            Component: PaymentCardsOnboardingDialogStep1Pricing,
        },
        [OnboardingStepEnum.STEP2]: {
            prev: {
                text: t`Back`,
                stateOnClick: () =>
                    Promise.resolve({
                        step: OnboardingStepEnum.STEP1,
                    }),
            },
            next: {
                text: t`Activate app`,
                isDisabled: (state: PaymentCardsOnboardingDialogState) => !isValidPaymentCardsOnboardingFormState(state.formData),
                stateOnClick: async state => {
                    // in case the user filled the CVR or country then we should update that
                    if (state.companyData.country !== state.formData.country || state.companyData.cvr !== state.formData.cvr) {
                        try {
                            const currentCompanyData = await api.getCurrentCompany();
                            currentCompanyData.model.cvr = state.formData.cvr;
                            currentCompanyData.model.country = state.formData.country;
                            await api.updateCurrentCompany(currentCompanyData.model);
                        } catch (err) {
                            return {
                                error: t`Failed to update company information`,
                            };
                        }
                    }

                    // activate the company
                    try {
                        await api.activateCompany();
                    } catch (err) {
                        return {
                            error: t`Failed to activate app`,
                        };
                    }
                    return Promise.resolve({
                        step: OnboardingStepEnum.STEP3,
                        error: '',
                        validationError: '',
                    });
                },
            },
            index: 2,
            Component: PaymentCardsOnboardingDialogCompanyStep2CompanyInformation,
        },
        [OnboardingStepEnum.STEP3]: {
            prev: {
                text: t`Close`,
                stateOnClick: state => {
                    onClose();
                    return Promise.resolve({
                        error: '',
                        validationError: '',
                        redirectUrl: state.walletUrl,
                    });
                },
            },
            next: {
                text: t`Continue to Mynt`,
                icon: 'link-external',
                stateOnClick: async state => {
                    if (!state.formData.agreeWithTerms) {
                        return {
                            error: t`You need to agree with the terms to continue`,
                        };
                    }
                    try {
                        const { kycUrl } = await api.getKYC();
                        window.open(kycUrl, '_blank');
                        onClose();
                    } catch (err: unknown) {
                        if (isAxiosError(err)) {
                            return { error: err.response?.data?.message };
                        }
                        return {
                            error: t`An error occurred.`,
                        };
                    }
                    return {
                        error: '',
                        validationError: '',
                        redirectUrl: state.walletUrl,
                    };
                },
            },
            index: 3,
            Component: PaymentCardsOnboardingDialogStep3Partner,
        },
    };

    return {
        steps: stepsConfig,
        getStepConfig: (step: OnboardingStepEnum) => {
            return stepsConfig[step];
        },
    };
};

export const step2GetInputProps = (state: PaymentCardsOnboardingDialogState) => {
    const { companyData, formData } = state;
    const inputs: Record<keyof CompanyInformation, CompanyInputProps<unknown>> = {
        cvr: {
            type: 'text',
            name: 'cvr',
            label: t`CVR number`,
            required: true,
            readOnly: false,
        },
        companyName: {
            type: 'text',
            name: 'companyName',
            label: t`Company name`,
            readOnly: true,
            required: true,
        },
        country: {
            type: 'text',
            name: 'country',
            label: t`Country`,
            required: true,
            readOnly: false,
        },
        agreeWithTerms: {
            type: 'checkbox',
            name: 'agreeWithTerms',
            required: true,
            disabled: false,
            label: t`I hereby consent to e-conomic sharing my company information with Mynt AS.`,
        },
    };

    if (companyData.country) {
        if (!isDenmark(companyData.country)) {
            inputs.country.error = t`This app is only available for companies registered in Denmark`;
            inputs.agreeWithTerms.disabled = true;
        }
        inputs.country.readOnly = true;
    } else if (!isDenmark(formData.country)) {
        inputs.country.readOnly = false;
        inputs.country.error = formData.country ? t`This app is only available for companies registered in Denmark` : t`Required`;
    }

    if (!companyData.cvr && !inputs.country.error) {
        inputs.cvr.readOnly = false;

        if (formData.cvr && !IsValidDanishCVR(formData.cvr)) {
            inputs.cvr.error = t`Please enter a valid CVR number containing 8 digits.`;
        } else if (!formData.cvr) {
            inputs.cvr.error = t`Required`;
        }
    } else {
        inputs.cvr.readOnly = true;
    }

    if (state.isLoading) {
        Object.keys(inputs).forEach(key => {
            inputs[key].readOnly = true;
        });
    }
    return inputs;
};

export type StepperConfig = ReturnType<typeof getStepConfig>;
